import { logError } from "utils/error";

/**
 * Converts a base64 data URL to a File object
 *
 * @param {string} dataurl - base64 data URL
 * @param {string} filename - desired name of the output file
 * @returns {File} A File object created from the base64 data URL
 */
const dataURLtoFile = (dataurl, filename) => {
  if (!dataurl || !filename) return;
  const arr = dataurl.split(","); // Split the data URL at the comma to separate metadata from data
  const mime = arr[0].match(/:(.*?);/)[1]; // Extract the MIME type from the data URL metadata
  const bstr = atob(arr[arr.length - 1]); // Decode the base64 string to binary data
  let n = bstr.length;
  const u8arr = new Uint8Array(n); // Create a typed array to hold the binary data

  // Populate the typed array with the binary data
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  // Return a new File object with the specified filename and MIME type
  return new File([u8arr], filename, { type: mime });
};

/**
 * Loads the video metadata and ensures the video element is ready for processing
 *
 * @param {HTMLVideoElement} videoElement - The video element to load data for
 * @returns {Promise} Resolves when video data is loaded or rejects on error
 */
const loadVideoData = (videoElement) => {
  return new Promise((resolve, reject) => {
    if (!(videoElement instanceof HTMLVideoElement)) {
      reject(new Error("Invalid video element provided."));
      return;
    }
    videoElement.onloadeddata = resolve; // Resolves the promise once video metadata is available
    videoElement.onerror = reject; // Rejects the promise if there’s an error loading video data
  });
};

/**
 * Seeks to a specific time in the video and resolves once the seek is complete
 *
 * @param {HTMLVideoElement} videoElement - The video element to seek
 * @param {number} captureTime - The time to seek to for the thumbnail capture (default = 1 second)
 * @returns {Promise} Resolves when the video seek is complete or rejects on error
 */
const seekVideoTime = (videoElement, captureTime = 1) => {
  return new Promise((resolve, reject) => {
    if (!(videoElement instanceof HTMLVideoElement)) {
      reject(new Error("Invalid video element provided."));
      return;
    }
    videoElement.onseeked = resolve; // Resolves when the seek operation completes
    videoElement.onerror = reject; // Rejects if there’s an error during the seek
    videoElement.currentTime = captureTime; // Set the video’s current time to the capture time
  });
};

/**
 * Extracts a thumbnail from a video file
 *
 * @param {File} videoFile - The video file from which to capture the thumbnail
 * @param {string} thumbnailFilename - The name to assign to the thumbnail file
 * @returns {Promise<File>} A promise that resolves with the thumbnail as a File object
 */
export const extractVideoThumbnail = async (videoFile, thumbnailFilename) => {
  try {
    const videoElement = document.createElement("video"); // Create a video element to load the video file
    const canvasElement = document.createElement("canvas"); // Create a canvas element to draw the thumbnail
    const videoURL = URL.createObjectURL(videoFile); // Create a URL for the video file

    videoElement.src = videoURL;
    videoElement.muted = true;
    videoElement.play(); // Start video playback to allow loading

    // Wait for the video data to load and seek to the specified capture time
    await loadVideoData(videoElement);
    await seekVideoTime(videoElement, 1); // Thumbnail capture time set to 1 second

    // Set canvas dimensions to match the video frame
    canvasElement.width = videoElement.videoWidth;
    canvasElement.height = videoElement.videoHeight;
    const ctx = canvasElement.getContext("2d");
    ctx.drawImage(
      videoElement,
      0,
      0,
      videoElement.videoWidth,
      videoElement.videoHeight
    ); // Draw the video frame onto the canvas

    const imageUrl = canvasElement.toDataURL("image/png"); // Convert the canvas image to a data URL

    // Clean up resources
    URL.revokeObjectURL(videoURL); // Revoke the video URL to free memory
    videoElement.remove(); // Remove the video element

    // Convert the data URL to a File object and return it
    return dataURLtoFile(imageUrl, thumbnailFilename);
  } catch (error) {
    logError({
      error,
      when: "extractVideoThumbnail",
      occuredAt: "features/Common/modules/File/utils/videoThumbnail.utils.js",
    });
  }
};
